import { Fragment, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import env from 'react-dotenv';
import {
    ArrowLeft, ArrowRight, Banknote, Building2, CheckCircle2, ChevronRight,
    ClipboardList, Coins, CreditCard, FileText, Hash, Keyboard, KeyRound, Landmark,
    Loader2, Minus, MonitorSmartphone, Moon, Palette, Plus, Printer, RefreshCw, ScanLine, Send,
    ShieldCheck, Smartphone, Sun, Ticket as TicketIcon, Timer, Wallet, WifiOff, X,
} from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';
import { Crypter } from '../../Functions/Security/Crypter';
import datas from '../../Assets/data.json';
import Logo from '../../Assets/EBCallLogo.png';
import BorneKeyboard, { KbdMode } from './BorneKeyboard';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface Device { BorneId: number; ApiKey: string; CodeAbonne: string; CodeFiliale: string; Libelle: string }
interface Origine { CodeOrigine: string; Libelle: string }
interface Denomination { CodeDevise?: string; TypeCoupure?: string; Valeur: number }
interface Operation {
    CodeOperation: string; Libelle: string; Description?: string; DureeMoyenneSec?: number;
    NecessiteKYC?: boolean; NecessiteMontant?: boolean; NecessiteOrigineFonds?: boolean;
    NecessiteBilletage?: boolean; NecessiteRDV?: boolean;
    CodeDevise?: string;
    Origines?: Origine[]; Denominations?: Denomination[];
}
interface Categorie { CodeCategorie: string; Libelle: string; Icone?: string; Couleur?: string; OrdreAffichage?: number; Operations: Operation[] }
interface TicketRes {
    CodeDemande?: any; CodeOperationLigne?: any; CodeT?: string; CodeS?: string;
    NumeroTicket?: string; LibelleService?: string;
    Position?: number; NbTicketsDevant?: number; TempsAttenteEstimeSec?: number;
    HeureAppelEstimee?: string; CodeOperation?: string;
}
interface BilletLigne { CodeDevise?: string; TypeCoupure?: string; Valeur: number; Quantite: number }
interface OpDraft {
    op: Operation;
    NumeroCompteSaisi?: string; MontantSaisi?: string; CodeOrigine?: string; Motif?: string;
    Billetage?: BilletLigne[];
}

/* ---------- Formulaires KYC (POST /api/Borne/Formulaires) ---------- */

interface KycChamp {
    CodeChamp: string; Libelle: string; TypeChamp: string;
    Obligatoire: boolean; Placeholder?: string; OptionsJson?: string; ValeurDefaut?: string;
}
interface FormTask {
    CodeForm: string; Libelle: string; Description?: string;
    Champs: KycChamp[]; CodeOperation: string; CodeOperationLigne?: any; CodeDemande?: any;
}

/** OptionsJson = tableau JSON de chaînes (même format que la console admin). */
function parseOptions(json: any): string[] {
    if (Array.isArray(json)) return json.map(String).filter(Boolean)
    if (!json) return []
    try {
        const v = JSON.parse(json)
        return Array.isArray(v) ? v.map(String).filter(Boolean) : []
    } catch { return [] }
}

/** Normalise les champs d'un formulaire (formes tolérées : {Champs}, tableau plat…). */
function normalizeChamps(result: any): KycChamp[] {
    let list: any[] = []
    if (Array.isArray(result)) {
        list = result.some((x) => x && (x.CodeChamp !== undefined || x.TypeChamp !== undefined))
            ? result.filter((x) => x && (x.CodeChamp !== undefined || x.TypeChamp !== undefined))
            : (result.find((x) => Array.isArray(x?.Champs))?.Champs || [])
    } else if (result && Array.isArray(result.Champs)) list = result.Champs
    return list.map((c: any) => ({
        CodeChamp: c?.CodeChamp ?? '', Libelle: c?.Libelle ?? '',
        TypeChamp: String(c?.TypeChamp ?? 'TEXT').toUpperCase(),
        Obligatoire: !!c?.Obligatoire,
        Placeholder: c?.Placeholder ?? '', OptionsJson: c?.OptionsJson ?? '',
        ValeurDefaut: c?.ValeurDefaut ?? '',
    }))
}

/* ---------- Champs dynamiques — générés depuis les flags Necessite* ---------- */

type FieldSpec =
    | { key: 'NumeroCompteSaisi' | 'MontantSaisi' | 'Motif'; type: 'text'; label: string; required: boolean; kbdMode: KbdMode; placeholder?: string }
    | { key: 'CodeOrigine'; type: 'choice'; label: string; required: boolean; options: Origine[] };

/** Référentiels livrés par le catalogue (ou embarqués par opération). */
interface Referentiels { origines: Origine[]; denominations: Denomination[] }

/** Construit la liste ordonnée des champs d'une opération selon sa configuration. */
function buildFields(op: Operation, refs: Referentiels): FieldSpec[] {
    const specs: FieldSpec[] = [
        { key: 'NumeroCompteSaisi', type: 'text', label: 'N° de compte', required: false, kbdMode: 'full', placeholder: 'CI05…' },
    ]
    if (op.NecessiteMontant)
        specs.push({ key: 'MontantSaisi', type: 'text', label: 'Montant', required: true, kbdMode: 'numeric', placeholder: '0' })
    if (op.NecessiteOrigineFonds)
        specs.push({ key: 'CodeOrigine', type: 'choice', label: 'Origine des fonds', required: true, options: op.Origines?.length ? op.Origines : refs.origines })
    specs.push({ key: 'Motif', type: 'text', label: 'Motif', required: false, kbdMode: 'full', placeholder: "Précisez l'objet de la visite" })
    return specs
}

/** Champ considéré vide (pour le blocage des champs requis). */
function fieldEmpty(d: OpDraft, f: FieldSpec): boolean {
    return !String((d as any)[f.key] || '').trim()
}

/** Coupures applicables à une opération (liste embarquée ou référentiel filtré par devise). */
function denomsFor(op: Operation, refs: Referentiels): Denomination[] {
    const den = op.Denominations?.length ? op.Denominations : refs.denominations
    return op.CodeDevise ? den.filter((d) => !d.CodeDevise || d.CodeDevise === op.CodeDevise) : den
}

type Step = 'pairing' | 'home' | 'catalogue' | 'operations' | 'details' | 'billetage' | 'confirm' | 'checkin' | 'forms' | 'ticket' | 'offline';

const DEVICE_KEY = 'eb-borne-device';
const TICKET_RESET_MS = 15000;

/** Result peut arriver objet ou sérialisé en chaîne JSON — on normalise. */
function parseResult<T = any>(v: any): T {
    if (typeof v === 'string') { try { return JSON.parse(v) } catch { } }
    return v;
}

/** Lit le device stocké — invalide s'il n'a pas d'ApiKey. */
function readDevice(): Device | null {
    try {
        const d = JSON.parse(localStorage.getItem(DEVICE_KEY) || 'null')
        return d && d.ApiKey ? d : null
    } catch { return null }
}

/** Icônes lucide mappées depuis le champ Icone du catalogue (fallback FileText). */
const ICON_MAP: Record<string, any> = {
    cash: Banknote, banknote: Banknote, banque: Landmark, bank: Landmark,
    carte: CreditCard, card: CreditCard, compte: Landmark, mobile: Smartphone,
    scan: ScanLine, virement: Send, transfert: Send, docs: ClipboardList,
    kyc: ShieldCheck, dossier: FileText, depot: Wallet, retrait: Banknote,
};

/** URL de la plateforme cliente en ligne (QR scanné par le client). */
const PLATEFORME_URL = env.PLATEFORME_URL || window.location.origin;

/** QR aux modules arrondis — toujours foncé sur pastille claire pour rester scannable. */
const BorneQRCode = ({ url }: { url: string }) => {
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!ref.current) return
        ref.current.innerHTML = ''
        new QRCodeStyling({
            width: 104, height: 104, data: url, margin: 0,
            qrOptions: { errorCorrectionLevel: 'M' },
            imageOptions: { crossOrigin: 'anonymous', margin: 2 },
            dotsOptions: { color: '#1c2340', type: 'rounded' },
            cornersSquareOptions: { color: '#1c2340', type: 'extra-rounded' },
            cornersDotOptions: { color: '#1c2340', type: 'dot' },
            backgroundOptions: { color: 'transparent' },
        }).append(ref.current)
    }, [url])
    return <div ref={ref} className="eb-borne-qr-canvas" />
};

function fmtWait(sec?: number) {
    if (sec == null) return '—';
    const m = Math.round(sec / 60);
    return m < 1 ? '< 1 min' : `${m} min`;
}

/** Montants et coupures au format fr-FR. */
function formatNombre(v: number) {
    return v.toLocaleString('fr-FR')
}

function CatIcon({ name }: { name?: string }) {
    const C = ICON_MAP[String(name || '').toLowerCase()] || FileText;
    return <C size={26} />;
}

/** Stepper horizontal — `done` optionnel (ex. rubrique remplie), étapes cliquables. */
const BorneFlowSteps = ({ steps, current, done, onJump }: {
    steps: string[]; current: number; done?: boolean[]; onJump?: (i: number) => void;
}) => (
    <nav className="eb-borne-flow">
        {steps.map((s, i) => {
            const isDone = done ? !!done[i] : i < current
            return (
                <Fragment key={s}>
                    <button type="button" disabled={i === current || !onJump}
                        onClick={() => i !== current && onJump?.(i)}
                        className={`eb-borne-flow-item${i === current ? ' is-cur' : ''}${isDone ? ' is-done' : ''}`}>
                        <span className="eb-borne-flow-dot">
                            {isDone ? <CheckCircle2 size={15} /> : i + 1}
                        </span>
                        <span className="eb-borne-flow-name">{s}</span>
                    </button>
                    {i < steps.length - 1 && (
                        <span className={`eb-borne-flow-line${isDone ? ' is-done' : ''}`} />
                    )}
                </Fragment>
            )
        })}
    </nav>
)

/** Salutation selon l'heure : Bonjour / Bon après-midi / Bonsoir. */
function salutation(): string {
    const h = new Date().getHours()
    if (h < 12) return 'Bonjour'
    if (h < 18) return 'Bon après-midi'
    return 'Bonsoir'
}

/* ------------------------------------------------------------------ */
/* Composant                                                           */
/* ------------------------------------------------------------------ */

const SelfServiceBorne = () => {
    const [device, setDevice] = useState<Device | null>(readDevice);

    const [step, setStep] = useState<Step>(device ? 'home' : 'pairing')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    // Pairing
    const [pairCode, setPairCode] = useState('')
    const [pairExpire, setPairExpire] = useState<Date | null>(null)
    const pairPoll = useRef<any>(null)

    // Catalogue & parcours
    const [catalogue, setCatalogue] = useState<Categorie[]>([])
    const [refs, setRefs] = useState<Referentiels>({ origines: [], denominations: [] })
    const [catSel, setCatSel] = useState<Categorie | null>(null)
    const [drafts, setDrafts] = useState<OpDraft[]>([])
    const [detIdx, setDetIdx] = useState(0)
    const [bilIdx, setBilIdx] = useState(0)
    const [tickets, setTickets] = useState<TicketRes[]>([])
    const [missed, setMissed] = useState<Record<string, boolean>>({})
    const resetTimer = useRef<any>(null)

    // Formulaires KYC post-check-in (file de formulaires à remplir)
    const [formTasks, setFormTasks] = useState<FormTask[]>([])
    const [formIdx, setFormIdx] = useState(0)
    const [formVals, setFormVals] = useState<Record<string, any>>({})
    const formValsRef = useRef(formVals); formValsRef.current = formVals

    // Check-in d'un ticket généré en ligne (QR token ou numéro)
    const [checkinCode, setCheckinCode] = useState('')
    const [ciMode, setCiMode] = useState<'choix' | 'saisir' | 'scan'>('choix')
    const [camErr, setCamErr] = useState('')
    const videoRef = useRef<HTMLVideoElement>(null)

    // Clavier virtuel : champ actif (mode + mise à jour fonctionnelle + aperçu)
    const [kbd, setKbd] = useState<{
        mode: KbdMode; apply: (fn: (v: string) => string) => void; onEnter?: () => void;
        getPreview?: () => string; previewLabel?: string; enterHint?: 'next' | 'done'
    } | null>(null)
    const openKbd = (mode: KbdMode, apply: (fn: (v: string) => string) => void, onEnter?: () => void,
        getPreview?: () => string, previewLabel?: string, enterHint?: 'next' | 'done') => {
        setKbd({ mode, apply, onEnter, getPreview, previewLabel, enterHint })
    }
    // Refs fraîches pour l'aperçu du clavier (évite les closures périmées)
    const checkinRef = useRef(checkinCode); checkinRef.current = checkinCode
    const draftsRef = useRef(drafts); draftsRef.current = drafts

    /** Clavier du code de check-in (avec aperçu live). */
    const kbdForCheckin = () =>
        openKbd('full', (fn) => setCheckinCode((p) => fn(p)), () => checkInOnlineTicket(),
            () => checkinRef.current, 'Code de réservation', 'done')

    /** Aplatit tous les champs de toutes les opérations — séquence de navigation « Suivant ». */
    const fieldSeq = () => drafts.flatMap((d) =>
        buildFields(d.op, refs).map((f) => ({ codeOp: d.op.CodeOperation, spec: f })))

    /** Donne le focus à un champ — clavier si texte, focus simple sinon. */
    const focusField = (codeOp: string, spec: FieldSpec) => {
        setCurField(`${codeOp}:${spec.key}`)
        if (spec.type === 'text') { kbdForDraft(codeOp, spec); return }
        setKbd(null)
        document.querySelector<HTMLElement>(`[data-borne-field="${codeOp}:${spec.key}"]`)?.focus()
    }

    /** Ouvre le clavier pour un champ texte — « Suivant » enchaîne sur le champ suivant. */
    const kbdForDraft = (codeOp: string, spec: Extract<FieldSpec, { type: 'text' }>) => {
        setCurField(`${codeOp}:${spec.key}`)
        const seq = fieldSeq()
        const next = seq[seq.findIndex((x) => x.codeOp === codeOp && x.spec.key === spec.key) + 1]
        openKbd(spec.kbdMode,
            (fn) => setDrafts((prev) =>
                prev.map((d) => d.op.CodeOperation === codeOp ? { ...d, [spec.key]: fn((d as any)[spec.key] || '') } : d)),
            next ? () => {
                // Champ d'une autre opération → la carte du stepper avance d'abord
                const ni = drafts.findIndex((d) => d.op.CodeOperation === next.codeOp)
                if (ni >= 0) setDetIdx(ni)
                setTimeout(() => focusField(next.codeOp, next.spec), 80)
            } : undefined,
            () => (draftsRef.current.find((d) => d.op.CodeOperation === codeOp) as any)?.[spec.key] || '',
            spec.label,
            next ? 'next' : 'done')
    }

    /* ---------- Billetage ---------- */

    const billQty = (d: OpDraft, dn: Denomination) =>
        d.Billetage?.find((b) => b.Valeur === dn.Valeur && b.CodeDevise === dn.CodeDevise && b.TypeCoupure === dn.TypeCoupure)?.Quantite || 0

    const setBillQty = (codeOp: string, dn: Denomination, qty: number) =>
        setDrafts((prev) => prev.map((d) => {
            if (d.op.CodeOperation !== codeOp) return d
            const rest = (d.Billetage || []).filter((b) =>
                !(b.Valeur === dn.Valeur && b.CodeDevise === dn.CodeDevise && b.TypeCoupure === dn.TypeCoupure))
            const lignes = qty > 0
                ? [...rest, { CodeDevise: dn.CodeDevise, TypeCoupure: dn.TypeCoupure, Valeur: dn.Valeur, Quantite: qty }]
                : rest
            return { ...d, Billetage: lignes }
        }))

    const billTotal = (d: OpDraft) => (d.Billetage || []).reduce((s, b) => s + b.Valeur * b.Quantite, 0)

    /** Libellé d'origine résolu depuis le référentiel. */
    const origineLibelle = (code?: string) => refs.origines.find((o) => o.CodeOrigine === code)?.Libelle || code

    /** Champs requis vides d'une seule opération. */
    const missingFor = (d: OpDraft) => {
        const missing: Record<string, boolean> = {}
        buildFields(d.op, refs).forEach((f) => {
            if (f.required && fieldEmpty(d, f)) missing[`${d.op.CodeOperation}:${f.key}`] = true
        })
        return missing
    }

    /** Affiche les champs manquants et renvoie false s'il y en a. */
    const showMissing = (missing: Record<string, boolean>) => {
        setMissed(missing)
        if (Object.keys(missing).length) {
            setError('Complétez les champs encadrés en rouge avant de continuer')
            document.querySelector<HTMLElement>(`[data-borne-field="${Object.keys(missing)[0]}"]`)?.focus()
            return false
        }
        setError('')
        return true
    }

    /** Validation complète — garde-fou avant le récapitulatif. */
    const validateDetails = () =>
        showMissing(drafts.reduce((acc, d) => ({ ...acc, ...missingFor(d) }), {}))

    /* ---------------- Stepper « Compléments » (une opération à la fois) ---------------- */

    const curDraft = drafts.length ? drafts[Math.min(detIdx, drafts.length - 1)] : undefined

    const goDetails = () => { setDetIdx(0); setStep('details') }
    const nextOp = () => {
        if (curDraft && showMissing(missingFor(curDraft)))
            setDetIdx((i) => Math.min(i + 1, drafts.length - 1))
    }
    const prevOp = () => setDetIdx((i) => Math.max(0, i - 1))

    /* ---------------- Étape « Billetage » (à part, après Compléments) ---------------- */

    const bilOps = drafts.filter((d) => d.op.NecessiteBilletage)
    const curBil = bilOps.length ? bilOps[Math.min(bilIdx, bilOps.length - 1)] : undefined

    /** Billetage vide alors que des coupures existent → bloquant. */
    const bilMissing = (d: OpDraft): Record<string, boolean> =>
        (denomsFor(d.op, refs).length > 0 && !(d.Billetage || []).some((b) => b.Quantite > 0))
            ? { [`${d.op.CodeOperation}:Billetage`]: true } : {}

    const goAfterDetails = () => {
        if (validateDetails()) { setBilIdx(0); setStep(bilOps.length ? 'billetage' : 'confirm') }
    }
    const nextBil = () => {
        if (curBil && showMissing(bilMissing(curBil)))
            setBilIdx((i) => Math.min(i + 1, bilOps.length - 1))
    }
    const prevBil = () => setBilIdx((i) => Math.max(0, i - 1))
    const validateBilletage = () =>
        showMissing(bilOps.reduce((acc, d) => ({ ...acc, ...bilMissing(d) }), {}))

    // Rubrique courante dans le stepper de l'opération (`codeOp:clé`)
    const [curField, setCurField] = useState('')

    // Thème borne : clair par défaut + accent personnalisable (persistés)
    const [theme, setTheme] = useState<'light' | 'dark'>(() =>
        (localStorage.getItem('eb-borne-theme') as 'dark' | null) === 'dark' ? 'dark' : 'light')
    const [accent, setAccent] = useState<string>(() => localStorage.getItem('eb-borne-accent') || '#4338ca')
    const [motifs, setMotifs] = useState<boolean>(() => localStorage.getItem('eb-borne-motifs') !== '0')
    const [openCfg, setOpenCfg] = useState(false)

    function setThemeMode(t: 'light' | 'dark') {
        setTheme(t)
        localStorage.setItem('eb-borne-theme', t)
    }
    function changeAccent(c: string) {
        setAccent(c)
        localStorage.setItem('eb-borne-accent', c)
    }
    function toggleMotifs() {
        const m = !motifs
        setMotifs(m)
        localStorage.setItem('eb-borne-motifs', m ? '1' : '0')
    }

    /* ---------------- Pairing ---------------- */

    async function initPairing() {
        setLoading(true); setError('')
        try {
            const res = await axios.post(env.API_BASE_URL + datas.API_LINKs.Borne.PairingInit, '', {
                headers: { 'Content-Type': 'application/json' },
            })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                const r = parseResult(v.Result)
                setPairCode(r.PairingCode)
                setPairExpire(new Date(r.PairingExpire))
                startPairingPoll(r.PairingCode)
            } else {
                setError(v.Message || 'Impossible de générer le code de pairing')
            }
        } catch (e: any) {
            setError(e.response?.status === 429 ? 'Trop de requêtes — patientez' : 'Serveur injoignable')
        }
        setLoading(false)
    }

    function startPairingPoll(code: string) {
        if (pairPoll.current) clearInterval(pairPoll.current)
        pairPoll.current = setInterval(async () => {
            try {
                const res = await axios.post(env.API_BASE_URL + datas.API_LINKs.Borne.PairingComplete,
                    Crypter.encrypt(JSON.stringify(code)),
                    { headers: { 'Content-Type': 'application/json' } })
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                    const d: Device = parseResult(v.Result)
                    if (!d?.ApiKey) return // payload inattendu — on continue le poll
                    clearInterval(pairPoll.current)
                    localStorage.setItem(DEVICE_KEY, JSON.stringify(d))
                    setDevice(d)
                    setStep('home')
                    loadCatalogue(d) // catalogue préchargé après enrôlement
                }
                // 404 = pas encore validé par l'admin → on continue le poll
            } catch { /* code non validé — on réessaie */ }
        }, 3000)
    }

    /* ---------------- Catalogue ---------------- */

    /** GET /api/Borne/Status — vérifie au démarrage que la borne est active. */
    async function checkStatus(dev: Device): Promise<boolean> {
        try {
            const res = await axios.get(env.API_BASE_URL + datas.API_LINKs.Borne.Status,
                { headers: { 'X-Borne-Key': dev.ApiKey } })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                const r = parseResult(v.Result)
                // Le contexte renvoyé peut rafraîchir les infos stockées (libellé, filiale…)
                if (r && (r.BorneId != null || r.Libelle || r.CodeFiliale)) {
                    const merged = { ...dev, ...r, ApiKey: r.ApiKey || dev.ApiKey }
                    localStorage.setItem(DEVICE_KEY, JSON.stringify(merged))
                    setDevice(merged)
                }
                return true
            }
            setError(v.Message || 'Borne inactive')
            return false
        } catch (e: any) {
            if (e.response?.status === 401) {
                // Clé révoquée → ré-enrôlement
                localStorage.removeItem(DEVICE_KEY)
                setDevice(null); setStep('pairing')
                return false
            }
            return true // injoignable → loadCatalogue basculera sur l'écran hors-ligne
        }
    }

    /** GET /api/Borne/Origines + /api/Borne/Denominations — référentiels de la borne. */
    async function loadReferentiels(dev: Device) {
        const getList = async (url: string): Promise<any[]> => {
            try {
                const res = await axios.get(env.API_BASE_URL + url,
                    { headers: { 'X-Borne-Key': dev.ApiKey, 'Accept': 'text/plain' } })
                const v = JSON.parse(Crypter.decrypt(res.data)).Value
                if (v && v.StatutCode != null && v.StatutCode != 200 && v.StatutCode !== 'OK') return []
                const r = parseResult(v?.Result ?? v)
                if (Array.isArray(r)) return r
                if (Array.isArray(r?.Table)) return r.Table
                if (Array.isArray(r?.Table1)) return r.Table1
            } catch { /* référentiel indisponible — champ en repli */ }
            return []
        }
        const [origines, denominations] = await Promise.all([
            getList(datas.API_LINKs.Borne.Origines),
            getList(datas.API_LINKs.Borne.Denominations), // CipherCodeDevise optionnel — toutes devises
        ])
        setRefs({ origines, denominations })
    }

    async function loadCatalogue(dev: Device | null = device) {
        if (!dev) return
        setLoading(true); setError('')
        try {
            const res = await axios.get(env.API_BASE_URL + datas.API_LINKs.Borne.Catalogue, {
                headers: { 'X-Borne-Key': dev.ApiKey },
            })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                const r = parseResult(v.Result)
                // Le Result peut être un tableau de catégories ou un objet
                // { Categories, Origines, Denominations } selon la version de l'API.
                const cats: Categorie[] = (Array.isArray(r) ? r : (r?.Categories ?? r?.Items ?? []))
                    .sort((a: Categorie, b: Categorie) => (a.OrdreAffichage || 0) - (b.OrdreAffichage || 0))
                setCatalogue(cats)
                if (!Array.isArray(r) && r) {
                    setRefs({
                        origines: r.Origines ?? r.OrigineFonds ?? [],
                        denominations: r.Denominations ?? r.Coupures ?? [],
                    })
                }
            } else {
                setError(v.Message || 'Catalogue indisponible')
            }
        } catch (e: any) {
            if (e.response?.status === 401) {
                // Borne révoquée/suspendue → retour au pairing
                localStorage.removeItem(DEVICE_KEY)
                setDevice(null); setStep('pairing')
            } else {
                setError('Catalogue inaccessible — vérifiez la connexion')
                setStep('offline')
            }
        }
        setLoading(false)
    }

    // Le clavier se ferme à chaque changement d'étape
    useEffect(() => { setKbd(null) }, [step])

    useEffect(() => {
        if (device) {
            // Parcours documenté : Status → Catalogue (+ Origines/Denominations)
            checkStatus(device).then((ok) => { if (ok) loadCatalogue() })
            loadReferentiels(device)
        }
        else initPairing()
        return () => {
            if (pairPoll.current) clearInterval(pairPoll.current)
            if (resetTimer.current) clearTimeout(resetTimer.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /* ---------------- Sélection & check-in ---------------- */

    function toggleOp(op: Operation) {
        setDrafts((prev) =>
            prev.some((d) => d.op.CodeOperation === op.CodeOperation)
                ? prev.filter((d) => d.op.CodeOperation !== op.CodeOperation)
                : [...prev, { op }]
        )
        setMissed((m) => Object.fromEntries(
            Object.entries(m).filter(([k]) => !k.startsWith(`${op.CodeOperation}:`))))
    }

    function patchDraft(codeOp: string, patch: Partial<OpDraft>) {
        setDrafts((prev) => prev.map((d) => d.op.CodeOperation === codeOp ? { ...d, ...patch } : d))
    }    async function checkIn() {
        if (!device || !drafts.length) return
        setLoading(true); setError('')
        const payload = {
            CodeClient: null,
            CodeTypeProfil: 'VISITEUR',
            Operations: drafts.map((d) => ({
                CodeOperation: d.op.CodeOperation,
                NumeroCompteSaisi: d.NumeroCompteSaisi || undefined,
                MontantSaisi: d.MontantSaisi ? Number(d.MontantSaisi) : undefined,
                CodeOrigine: d.CodeOrigine || undefined,
                Motif: d.Motif || undefined,
            })),
        }
        try {
            const res = await axios.post(env.API_BASE_URL + datas.API_LINKs.Borne.CheckIn,
                Crypter.encrypt(JSON.stringify(payload)),
                { headers: { 'Content-Type': 'application/json', 'X-Borne-Key': device.ApiKey } })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                const r = parseResult(v.Result)
                const items: any[] = Array.isArray(r) ? r : [r]
                setTickets(items)
                // CodeOperationLigne rattache chaque opération à sa ligne de demande
                const ligneOf = (codeOp: string) =>
                    items.find((t: any) => t.CodeOperation === codeOp)?.CodeOperationLigne
                const codeDemande = items.find((t: any) => t.CodeDemande)?.CodeDemande

                // Formulaires KYC des opérations concernées → étape dédiée
                const tasks: FormTask[] = []
                for (const d of drafts.filter((x) => x.op.NecessiteKYC)) {
                    tasks.push(...await fetchForms(d.op.CodeOperation, ligneOf(d.op.CodeOperation), codeDemande))
                }
                if (tasks.length) {
                    setFormTasks(tasks); setFormIdx(0); setFormVals({}); setMissed({})
                    setStep('forms')
                } else {
                    await submitBilletages(ligneOf)
                    setStep('ticket')
                    // Retour automatique à l'accueil borne
                    resetTimer.current = setTimeout(resetFlow, TICKET_RESET_MS)
                }
            } else {
                setError(v.Message || 'Check-in refusé')
            }
        } catch (e: any) {
            if (e.response?.status === 401) {
                localStorage.removeItem(DEVICE_KEY)
                setDevice(null); setStep('pairing')
            } else {
                setError(e.response?.status === 429 ? 'Trop de requêtes — patientez' : 'Échec du check-in — réessayez')
            }
        }
        setLoading(false)
    }

    /** POST /api/Borne/Formulaires — formulaires (KYC…) requis par une opération. */
    async function fetchForms(codeOp: string, codeLigne: any, codeDemande: any): Promise<FormTask[]> {
        if (!device) return []
        try {
            const res = await axios.post(env.API_BASE_URL + datas.API_LINKs.Borne.Formulaires,
                Crypter.encrypt(JSON.stringify({ CodeOperation: codeOp })),
                { headers: { 'Content-Type': 'application/json', 'X-Borne-Key': device.ApiKey } })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode != 200 && v.StatutCode !== 'OK') return []
            const r = parseResult(v.Result)
            const forms = Array.isArray(r) ? r : (r?.Formulaires ?? (r ? [r] : []))
            return forms.map((f: any) => {
                const head = f.Formulaire && typeof f.Formulaire === 'object' ? f.Formulaire : f
                return {
                    CodeForm: head.CodeForm ?? '', Libelle: head.Libelle ?? 'Formulaire',
                    Description: head.Description, Champs: normalizeChamps(f),
                    CodeOperation: codeOp, CodeOperationLigne: codeLigne, CodeDemande: codeDemande,
                } as FormTask
            }).filter((f: FormTask) => f.CodeForm && f.Champs.length)
        } catch { return [] }
    }

    /** POST /api/Borne/Billetage — déclare le billetage de chaque ligne concernée. */
    async function submitBilletages(ligneOf: (codeOp: string) => any) {
        if (!device) return
        for (const d of drafts) {
            const lignes = (d.Billetage || []).filter((b) => b.Quantite > 0)
            if (!d.op.NecessiteBilletage || !lignes.length) continue
            try {
                await axios.post(env.API_BASE_URL + datas.API_LINKs.Borne.Billetage,
                    Crypter.encrypt(JSON.stringify({
                        CodeOperationLigne: ligneOf(d.op.CodeOperation),
                        MontantSaisi: d.MontantSaisi ? Number(d.MontantSaisi) : billTotal(d),
                        Lignes: lignes,
                    })),
                    { headers: { 'Content-Type': 'application/json', 'X-Borne-Key': device.ApiKey } })
            } catch { /* échec silencieux — le ticket reste valide */ }
        }
    }

    /** POST /api/Borne/FormulaireSubmit — envoie le formulaire courant puis enchaîne. */
    async function submitForm(task: FormTask) {
        const missing: Record<string, boolean> = {}
        task.Champs.forEach((c) => {
            const val = formVals[c.CodeChamp]
            if (c.Obligatoire && (val === undefined || val === '' || val === null)) missing[c.CodeChamp] = true
        })
        setMissed(missing)
        if (Object.keys(missing).length) { setError('Complétez les champs requis'); return }
        setError(''); setLoading(true)
        try {
            const res = await axios.post(env.API_BASE_URL + datas.API_LINKs.Borne.FormulaireSubmit,
                Crypter.encrypt(JSON.stringify({
                    CodeForm: task.CodeForm, CodeDemande: task.CodeDemande,
                    CodeOperationLigne: task.CodeOperationLigne,
                    ReponseJson: JSON.stringify(formVals),
                })),
                { headers: { 'Content-Type': 'application/json', 'X-Borne-Key': device!.ApiKey } })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                if (formIdx < formTasks.length - 1) {
                    setFormIdx((i) => i + 1); setFormVals({}); setMissed({})
                } else {
                    const ligneOf = (codeOp: string) =>
                        tickets.find((t: any) => t.CodeOperation === codeOp)?.CodeOperationLigne
                    await submitBilletages(ligneOf)
                    setStep('ticket')
                    resetTimer.current = setTimeout(resetFlow, TICKET_RESET_MS)
                }
            } else {
                setError(v.Message || 'Envoi du formulaire refusé')
            }
        } catch { setError("Échec de l'envoi — réessayez") }
        setLoading(false)
    }

    /** Clavier d'un champ de formulaire KYC (numérique pour NUMBER). */
    const kbdForForm = (ch: KycChamp) => {
        setCurField(`form:${ch.CodeChamp}`)
        openKbd(ch.TypeChamp === 'NUMBER' ? 'numeric' : 'full',
            (fn) => setFormVals((p) => ({ ...p, [ch.CodeChamp]: fn(String(p[ch.CodeChamp] ?? '')) })),
            undefined,
            () => String(formValsRef.current[ch.CodeChamp] ?? ''),
            ch.Libelle, 'done')
    }

    /** Check-in d'un ticket réservé en ligne (statut '0' → '1'). */
    async function checkInOnlineTicket(codeOverride?: string) {
        const code = (codeOverride ?? checkinCode).trim()
        if (!device || !code) { setError('Saisissez votre code de ticket'); return }
        setLoading(true); setError('')
        // Le code peut être un QrToken (long) ou un numéro de ticket (court, GS1-007)
        const payload = code.length > 24
            ? { QrToken: code, CodeFiliale: device.CodeFiliale }
            : { CodeT: code, CodeFiliale: device.CodeFiliale }
        try {
            const res = await axios.post(env.API_BASE_URL + datas.API_LINKs.Demandes.CheckInTicket,
                Crypter.encrypt(JSON.stringify(payload)),
                { headers: { 'Content-Type': 'application/json', 'X-Borne-Key': device.ApiKey } })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode == 200 || v.StatutCode === 'OK') {
                const r = parseResult(v.Result)
                const list = Array.isArray(r) ? r : (r?.Ticket || r?.Tickets || [r])
                setTickets(list.filter(Boolean))
                setCheckinCode('')
                setStep('ticket')
                resetTimer.current = setTimeout(resetFlow, TICKET_RESET_MS)
            } else {
                setError(v.Message || 'Ticket introuvable ou déjà confirmé')
            }
        } catch (e: any) {
            if (e.response?.status === 401) {
                localStorage.removeItem(DEVICE_KEY)
                setDevice(null); setStep('pairing')
            } else {
                setError('Échec du check-in — vérifiez le code et réessayez')
            }
        }
        setLoading(false)
    }

    /** GET /api/Billetage/Bordereau — PDF du billetage déclaré (même demande, jour même). */
    async function imprimerBordereau() {
        const codeDemande = tickets.find((t) => t.CodeDemande)?.CodeDemande
        if (!device || !codeDemande) return
        try {
            const res = await axios.get(
                env.API_BASE_URL + datas.API_LINKs.Billetage.Bordereau + Crypter.encrypt(codeDemande),
                { headers: { 'X-Borne-Key': device.ApiKey, 'Accept': 'text/plain' } })
            const v = JSON.parse(Crypter.decrypt(res.data)).Value
            if (v.StatutCode == 200 && v.Result?.Base64) {
                const bytes = Uint8Array.from(atob(v.Result.Base64), (c) => c.charCodeAt(0))
                const blob = new Blob([bytes], { type: v.Result.ContentType || 'application/pdf' })
                window.open(URL.createObjectURL(blob), '_blank')
            }
        } catch { /* impression indisponible — non bloquant */ }
    }

    function resetFlow() {
        setDrafts([]); setCatSel(null); setTickets([]); setError(''); setCheckinCode('')
        setCiMode('choix'); setMissed({}); setDetIdx(0); setBilIdx(0)
        setFormTasks([]); setFormIdx(0); setFormVals({})
        setStep('home')
    }

    /* ---------------- Scanner (caméra + lecteur HID) ---------------- */

    useEffect(() => {
        if (step !== 'checkin' || ciMode !== 'scan') return
        let stream: MediaStream | null = null
        let timer: any = null
        let disposed = false

        // Lecteur code-barres HID : frappe rapide suivie d'Enter = scan matériel
        let buf = ''
        let last = 0
        const onKey = (e: KeyboardEvent) => {
            const now = Date.now()
            if (now - last > 80) buf = ''
            last = now
            if (e.key === 'Enter') {
                if (buf.length >= 3) checkInOnlineTicket(buf)
                buf = ''
            } else if (e.key.length === 1) {
                buf += e.key
            }
        }
        document.addEventListener('keydown', onKey)

        // Caméra via BarcodeDetector natif (Chrome/Edge)
        const BD = (window as any).BarcodeDetector
        if (BD && navigator.mediaDevices?.getUserMedia) {
            (async () => {
                try {
                    const det = new BD({ formats: ['qr_code', 'data_matrix', 'code_128', 'code_39', 'ean_13'] })
                    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
                    if (disposed) { stream?.getTracks().forEach((t) => t.stop()); return }
                    if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play() }
                    timer = setInterval(async () => {
                        try {
                            const codes = await det.detect(videoRef.current!)
                            if (codes.length) checkInOnlineTicket(codes[0].rawValue)
                        } catch { }
                    }, 350)
                } catch {
                    setCamErr('Caméra indisponible — présentez le code devant le lecteur de la borne.')
                }
            })()
        } else {
            setCamErr('Caméra indisponible — présentez le code devant le lecteur de la borne.')
        }

        return () => {
            disposed = true
            document.removeEventListener('keydown', onKey)
            if (timer) clearInterval(timer)
            stream?.getTracks().forEach((t) => t.stop())
            if (videoRef.current) videoRef.current.srcObject = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, ciMode])

    /* ---------------- Rendu ---------------- */

    return (
        <div className={`eb-borne${theme === 'dark' ? ' is-dark' : ''}${motifs ? '' : ' no-motifs'}`}
            style={{ ['--eb-primary' as any]: accent }}>
            {motifs && <><span className="eb-borne-orb o1" /><span className="eb-borne-orb o2" /></>}

            {/* Bouton flottant — configuration de l'apparence */}
            <button className="eb-borne-themebtn" onClick={() => setOpenCfg(!openCfg)} title="Personnaliser l'apparence">
                <Palette size={18} />
            </button>

            {/* Mini-dialogue de configuration */}
            {openCfg && (
                <div className="eb-borne-cfg">
                    <div className="eb-borne-cfg-head">
                        <span>Apparence</span>
                        <button onClick={() => setOpenCfg(false)}><X size={15} /></button>
                    </div>
                    <div className="eb-borne-cfg-row">
                        <label>Mode</label>
                        <div className="eb-borne-cfg-modes">
                            <button className={theme === 'light' ? 'is-on' : ''} onClick={() => setThemeMode('light')}>
                                <Sun size={15} /> Clair
                            </button>
                            <button className={theme === 'dark' ? 'is-on' : ''} onClick={() => setThemeMode('dark')}>
                                <Moon size={15} /> Sombre
                            </button>
                        </div>
                    </div>
                    <div className="eb-borne-cfg-row">
                        <label>Couleur d'accent</label>
                        <div className="eb-borne-cfg-swatches">
                            {['#4338ca', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'].map((c) => (
                                <button key={c} className={`eb-borne-swatch${accent === c ? ' is-on' : ''}`}
                                    style={{ background: c }} onClick={() => changeAccent(c)} />
                            ))}
                            <label className="eb-borne-swatch custom" style={{ background: accent }}>
                                <input type="color" value={accent} onChange={(e) => changeAccent(e.target.value)} />
                            </label>
                        </div>
                    </div>
                    <div className="eb-borne-cfg-row">
                        <label>Motifs de fond</label>
                        <button className={`eb-borne-cfg-toggle${motifs ? ' is-on' : ''}`} onClick={toggleMotifs}>
                            <span />
                        </button>
                    </div>
                </div>
            )}

            {/* Barre haute */}
            <header className="eb-borne-bar">
                <div className="eb-borne-brand">
                    <span>eBCall <b>Borne</b></span>
                </div>
                <div className="eb-borne-bar-right">
                    {device && <span className="eb-borne-device">{device.Libelle || `Borne #${device.BorneId}`}</span>}
                    {step !== 'pairing' && step !== 'home' && step !== 'ticket' && (
                        <button className="eb-borne-btn ghost" onClick={resetFlow}>
                            <X size={16} /> Annuler
                        </button>
                    )}
                </div>
            </header>


            {/* ---------------- Pairing ---------------- */}
            {step === 'pairing' && (
                <div className="eb-borne-stage">
                    <div className="eb-borne-card pairing">
                        <div className="eb-borne-pair-icon"><img src={Logo} alt="eBCall" className="eb-borne-pair-logo" /></div>
                        <h1>Enrôlement de la borne</h1>
                        <p className="eb-borne-sub">
                            Saisissez ce code dans la console d'administration<br />
                            (Paramètres → Gestion des bornes)
                        </p>
                        {pairCode ? (
                            <>
                                <div className="eb-borne-paircode">{pairCode}</div>
                                <p className="eb-borne-sub muted">
                                    <Loader2 size={14} className="animate-spin" /> En attente de validation…
                                    {pairExpire && ` (expire ${pairExpire.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})`}
                                </p>
                            </>
                        ) : loading ? (
                            <Loader2 size={34} className="animate-spin" />
                        ) : (
                            <button className="eb-borne-btn xl" onClick={initPairing}>
                                <Hash size={18} /> Générer le code de pairing
                            </button>
                        )}
                        {error && <p className="eb-borne-error">{error}</p>}
                    </div>
                </div>
            )}

            {/* ---------------- Offline ---------------- */}
            {step === 'offline' && (
                <div className="eb-borne-stage">
                    <div className="eb-borne-card pairing">
                        <div className="eb-borne-pair-icon warn"><WifiOff size={40} /></div>
                        <h1>Connexion interrompue</h1>
                        <p className="eb-borne-sub">{error}</p>
                        <button className="eb-borne-btn xl" onClick={() => loadCatalogue()}>
                            <RefreshCw size={18} /> Réessayer
                        </button>
                    </div>
                </div>
            )}

            {/* ---------------- Accueil : deux choix ---------------- */}
            {step === 'home' && (
                <div className="eb-borne-stage is-centered">
                    <div className="eb-borne-hero">
                        <img src={Logo} alt="eBCall" className="eb-borne-home-logo" />
                        <h1>{salutation()} !</h1>
                        <p>Bienvenue — que souhaitez-vous faire ?</p>
                    </div>
                    <div className="eb-borne-choices">
                        <button className="eb-borne-choice primary" onClick={() => { setError(''); setStep('catalogue') }}>
                            <span className="eb-borne-choice-icon"><TicketIcon size={34} /></span>
                            <span className="eb-borne-choice-txt">
                                <span className="eb-borne-choice-label">Tirer un nouveau ticket</span>
                                <span className="eb-borne-choice-sub">Choisissez vos opérations et recevez un ticket</span>
                            </span>
                            <ChevronRight size={22} className="eb-borne-choice-arrow" />
                        </button>
                        <button className="eb-borne-choice" onClick={() => { setError(''); setStep('checkin') }}>
                            <span className="eb-borne-choice-icon"><ScanLine size={34} /></span>
                            <span className="eb-borne-choice-txt">
                                <span className="eb-borne-choice-label">J'ai un ticket réservé</span>
                                <span className="eb-borne-choice-sub">Scannez ou saisissez le code de votre réservation en ligne</span>
                            </span>
                            <ChevronRight size={22} className="eb-borne-choice-arrow" />
                        </button>
                    </div>
                    <div className="eb-borne-qr">
                        <div className="eb-borne-qr-img">
                            <BorneQRCode url={PLATEFORME_URL} />
                        </div>
                        <div className="eb-borne-qr-txt">
                            <span className="eb-borne-qr-label"><Smartphone size={15} /> Réservez en ligne</span>
                            <span className="eb-borne-qr-sub">Scannez ce code pour prendre votre ticket depuis votre téléphone — plus besoin d'attendre devant la borne.</span>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------- Catalogue : catégories ---------------- */}
            {step === 'catalogue' && (
                <div className="eb-borne-stage is-centered">
                    <div className="eb-borne-hero sm">
                        <button className="eb-borne-back" onClick={() => setStep('home')}><ArrowLeft size={18} /> Accueil</button>
                        <h1>{salutation()}</h1>
                        <p>Quel type d'opération souhaitez-vous effectuer ?</p>
                    </div>
                    {loading ? (
                        <div className="eb-borne-loading">
                            <Loader2 size={40} className="animate-spin" />
                            <span>Chargement du catalogue…</span>
                        </div>
                    ) : (
                        <div className="eb-borne-cats">
                            {catalogue.map((c, i) => (
                                <button key={c.CodeCategorie} className="eb-borne-cat"
                                    style={{ ['--cat' as any]: c.Couleur || 'var(--eb-primary)', animationDelay: `${i * 60}ms` }}
                                    onClick={() => { setCatSel(c); setStep('operations') }}>
                                    <span className="eb-borne-cat-icon"><CatIcon name={c.Icone} /></span>
                                    <span className="eb-borne-cat-label">{c.Libelle}</span>
                                    <span className="eb-borne-cat-count">{c.Operations?.length || 0} opération{(c.Operations?.length || 0) > 1 ? 's' : ''}</span>
                                    <ChevronRight size={20} className="eb-borne-cat-arrow" />
                                </button>
                            ))}
                            {!catalogue.length && <p className="eb-borne-sub">Catalogue vide.</p>}
                        </div>
                    )}
                </div>
            )}

            {/* ---------------- Check-in : choix du mode ---------------- */}
            {step === 'checkin' && ciMode === 'choix' && (
                <div className="eb-borne-stage is-centered">
                    <div className="eb-borne-hero">
                        <div className="eb-borne-ticket-icon"><TicketIcon size={40} /></div>
                        <h1>J'ai déjà un ticket</h1>
                        <p>Comment souhaitez-vous confirmer votre arrivée ?</p>
                    </div>
                    <div className="eb-borne-choices">
                        <button className="eb-borne-choice primary" onClick={() => { setError(''); setCiMode('saisir') }}>
                            <span className="eb-borne-choice-icon"><Keyboard size={34} /></span>
                            <span className="eb-borne-choice-txt">
                                <span className="eb-borne-choice-label">Saisir mon code</span>
                                <span className="eb-borne-choice-sub">Numéro de ticket (GS1-007) ou code de réservation</span>
                            </span>
                            <ChevronRight size={22} className="eb-borne-choice-arrow" />
                        </button>
                        <button className="eb-borne-choice" onClick={() => { setError(''); setCamErr(''); setKbd(null); setCiMode('scan') }}>
                            <span className="eb-borne-choice-icon"><ScanLine size={34} /></span>
                            <span className="eb-borne-choice-txt">
                                <span className="eb-borne-choice-label">Scanner mon QR code</span>
                                <span className="eb-borne-choice-sub">Présentez le QR de votre réservation à la caméra</span>
                            </span>
                            <ChevronRight size={22} className="eb-borne-choice-arrow" />
                        </button>
                    </div>
                    <button className="eb-borne-btn ghost" style={{ marginTop: 26 }} onClick={() => { setError(''); setStep('home') }}>
                        <ArrowLeft size={17} /> Accueil
                    </button>
                </div>
            )}

            {/* ---------------- Check-in : saisie du code ---------------- */}
            {step === 'checkin' && ciMode === 'saisir' && (
                <div className="eb-borne-stage ci-saisir">
                    <div className="eb-borne-card pairing checkin">
                        <h1>Votre code de réservation</h1>
                        <p className="eb-borne-sub">Numéro de ticket (GS1-007) ou token de réservation</p>
                        <input
                            className="eb-borne-checkin-input"
                            autoFocus
                            readOnly
                            inputMode="none"
                            placeholder="GS1-007 ou token QR"
                            value={checkinCode}
                            onFocus={kbdForCheckin}
                            onClick={kbdForCheckin}
                            onKeyDown={(e) => { if (e.key === 'Enter') checkInOnlineTicket() }}
                        />
                        {error && <p className="eb-borne-error">{error}</p>}
                        <div className="flex gap-3">
                            <button className="eb-borne-btn ghost" onClick={() => { setError(''); setKbd(null); setCiMode('choix') }}>
                                <ArrowLeft size={17} /> Retour
                            </button>
                            <button className="eb-borne-btn xl" disabled={loading} onClick={() => checkInOnlineTicket()}>
                                {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                                Confirmer mon arrivée
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------- Check-in : scan du QR ---------------- */}
            {step === 'checkin' && ciMode === 'scan' && (
                <div className="eb-borne-stage is-centered">
                    <div className="eb-borne-hero sm">
                        <button className="eb-borne-back" onClick={() => { setError(''); setCamErr(''); setCiMode('choix') }}>
                            <ArrowLeft size={18} /> Retour
                        </button>
                        <h1>Scannez votre QR code</h1>
                        <p>{camErr || 'Présentez le code devant la caméra ou le lecteur de la borne'}</p>
                    </div>
                    <div className="eb-borne-scan">
                        <div className="eb-borne-scan-frame lg">
                            <video ref={videoRef} className="eb-borne-scan-video" muted playsInline />
                            <span className="eb-borne-scan-line" />
                            <span className="eb-borne-scan-corner tl" /><span className="eb-borne-scan-corner tr" />
                            <span className="eb-borne-scan-corner bl" /><span className="eb-borne-scan-corner br" />
                        </div>
                        {loading && <div className="eb-borne-loading"><Loader2 size={28} className="animate-spin" /><span>Vérification du ticket…</span></div>}
                        {error && <p className="eb-borne-error">{error}</p>}
                    </div>
                </div>
            )}

            {/* ---------------- Opérations (multi-sélection) ---------------- */}
            {step === 'operations' && catSel && (
                <div className="eb-borne-stage">
                    <div className="eb-borne-hero sm">
                        <button className="eb-borne-back" onClick={() => setStep('catalogue')}><ArrowLeft size={18} /> Catégories</button>
                        <h1>{catSel.Libelle}</h1>
                        <p>Sélectionnez une ou plusieurs opérations</p>
                    </div>
                    <div className="eb-borne-ops">
                        {catSel.Operations.map((o, i) => {
                            const sel = drafts.some((d) => d.op.CodeOperation === o.CodeOperation)
                            return (
                                <button key={o.CodeOperation}
                                    className={`eb-borne-op${sel ? ' is-sel' : ''}`}
                                    style={{ ['--cat' as any]: catSel.Couleur || 'var(--eb-primary)', animationDelay: `${i * 50}ms` }}
                                    onClick={() => toggleOp(o)}>
                                    <span className="eb-borne-op-check"><CheckCircle2 size={18} /></span>
                                    <span className="eb-borne-op-title">{o.Libelle}</span>
                                    {o.Description && <span className="eb-borne-op-desc">{o.Description}</span>}
                                    <span className="eb-borne-op-meta">
                                        {o.DureeMoyenneSec ? <><Timer size={13} /> ~{fmtWait(o.DureeMoyenneSec)}</> : null}
                                        {o.NecessiteMontant && <em>Montant</em>}
                                        {o.NecessiteOrigineFonds && <em>Origine fonds</em>}
                                        {o.NecessiteBilletage && <em>Billetage</em>}
                                        {o.NecessiteRDV && <em>Sur RDV</em>}
                                    </span>
                                </button>
                            )
                        })}
                    </div>
                    {drafts.length > 0 && (
                        <div className="eb-borne-dock">
                            <span>{drafts.length} opération{drafts.length > 1 ? 's' : ''} sélectionnée{drafts.length > 1 ? 's' : ''}</span>
                            <button className="eb-borne-btn xl" onClick={goDetails}>
                                Continuer <ArrowRight size={18} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ---------------- Détails : stepper horizontal (une opération à la fois) ---------------- */}
            {step === 'details' && curDraft && (
                <div className="eb-borne-stage">
                    <div className="eb-borne-hero sm">
                        <button className="eb-borne-back" onClick={() => setStep('operations')}><ArrowLeft size={18} /> Opérations</button>
                        <h1>Compléments</h1>
                        <p>Complétez chaque opération — les champs marqués sont requis</p>
                    </div>
                    <div className="eb-borne-details">
                        {(() => { const d = curDraft; const specs = buildFields(d.op, refs)
                            const curIdx = Math.max(0, specs.findIndex((f) => curField === `${d.op.CodeOperation}:${f.key}`))
                            return (
                            <div key={d.op.CodeOperation} className="eb-borne-detail-card">
                                <h3>
                                    {d.op.Libelle}
                                    {drafts.length > 1 && <em className="eb-borne-op-pos">{detIdx + 1}/{drafts.length}</em>}
                                </h3>
                                {specs.length > 1 && (
                                    <BorneFlowSteps
                                        steps={specs.map((f) => f.label)}
                                        current={curIdx}
                                        done={specs.map((f) => !fieldEmpty(d, f))}
                                        onJump={(i) => focusField(d.op.CodeOperation, specs[i])}
                                    />
                                )}
                                {specs.map((f) => {
                                    const fid = `${d.op.CodeOperation}:${f.key}`
                                    const miss = !!missed[fid]
                                    const clearMiss = () => setMissed((m) => (m[fid] ? { ...m, [fid]: false } : m))

                                    if (f.type === 'choice') return (
                                        <div key={f.key} className={`eb-borne-field${miss ? ' is-miss' : ''}`}>
                                            <label>{f.label}{f.required ? '' : ' (optionnel)'}</label>
                                            <div className="eb-borne-chips" role="radiogroup" tabIndex={-1} data-borne-field={fid}
                                                onFocus={() => setCurField(fid)}>
                                                {f.options.map((o) => (
                                                    <button key={o.CodeOrigine} type="button"
                                                        className={`eb-borne-chip${d.CodeOrigine === o.CodeOrigine ? ' is-sel' : ''}`}
                                                        onClick={() => { patchDraft(d.op.CodeOperation, { CodeOrigine: o.CodeOrigine }); clearMiss() }}>
                                                        {o.Libelle}
                                                        {d.CodeOrigine === o.CodeOrigine && <CheckCircle2 size={15} />}
                                                    </button>
                                                ))}
                                                {!f.options.length && <span className="muted">Aucune origine configurée</span>}
                                            </div>
                                        </div>
                                    )

                                    return (
                                        <div key={f.key} className={`eb-borne-field${miss ? ' is-miss' : ''}`}>
                                            <label>{f.label}{f.required ? '' : ' (optionnel)'}</label>
                                            <input value={(d as any)[f.key] || ''} placeholder={f.placeholder}
                                                readOnly inputMode="none" data-borne-field={fid}
                                                onFocus={() => { kbdForDraft(d.op.CodeOperation, f); clearMiss() }}
                                                onClick={() => { kbdForDraft(d.op.CodeOperation, f); clearMiss() }} />
                                        </div>
                                    )
                                })}
                            </div>
                        ); })()}
                    </div>
                    {error && <p className="eb-borne-error">{error}</p>}
                    <div className="eb-borne-dock">
                        {detIdx > 0 && (
                            <button className="eb-borne-btn ghost" onClick={prevOp}>
                                <ArrowLeft size={17} /> Précédent
                            </button>
                        )}
                        {detIdx < drafts.length - 1 ? (
                            <button className="eb-borne-btn xl" onClick={nextOp}>
                                Opération suivante <ArrowRight size={18} />
                            </button>
                        ) : (
                            <button className="eb-borne-btn xl" onClick={goAfterDetails}>
                                {bilOps.length ? 'Billetage' : 'Récapitulatif'} <ArrowRight size={18} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ---------------- Billetage (étape à part) ---------------- */}
            {step === 'billetage' && curBil && (() => {
                const d = curBil
                const denoms = denomsFor(d.op, refs)
                const groups = Object.entries(
                    denoms.reduce<Record<string, Denomination[]>>((acc, dn) => {
                        (acc[dn.TypeCoupure || 'AUTRE'] ||= []).push(dn)
                        return acc
                    }, {}))
                const fid = `${d.op.CodeOperation}:Billetage`
                const clearMiss = () => setMissed((m) => (m[fid] ? { ...m, [fid]: false } : m))
                return (
                    <div className="eb-borne-stage">
                        <div className="eb-borne-hero sm">
                            <button className="eb-borne-back" onClick={() => setStep('details')}><ArrowLeft size={18} /> Compléments</button>
                            <h1>Billetage</h1>
                            <p>Répartissez le montant en coupures</p>
                        </div>
                        {bilOps.length > 1 && (
                            <BorneFlowSteps
                                steps={bilOps.map((x) => x.op.Libelle)}
                                current={bilIdx}
                                done={bilOps.map((x) => billTotal(x) > 0)}
                                onJump={setBilIdx}
                            />
                        )}
                        <div className="eb-borne-details">
                            <div className="eb-borne-detail-card">
                                <h3>
                                    {d.op.Libelle}
                                    {bilOps.length > 1 && <em className="eb-borne-op-pos">{bilIdx + 1}/{bilOps.length}</em>}
                                </h3>
                                {d.MontantSaisi && (
                                    <p className="eb-borne-sub" style={{ margin: 0 }}>
                                        Montant déclaré : <b>{formatNombre(Number(d.MontantSaisi))}</b>
                                    </p>
                                )}
                                <div className={`eb-borne-field${missed[fid] ? ' is-miss' : ''}`}>
                                    <div className="eb-borne-bill" tabIndex={-1} data-borne-field={fid}>
                                        {groups.map(([type, list]) => (
                                            <div key={type} className="eb-borne-bill-group">
                                                {groups.length > 1 && (
                                                    <div className="eb-borne-bill-group-title">
                                                        {type === 'BILLET' ? 'Billets' : type === 'PIECE' ? 'Pièces' : 'Autres'}
                                                    </div>
                                                )}
                                                <div className="eb-borne-bill-grid">
                                                    {[...list].sort((a, b) => b.Valeur - a.Valeur).map((dn) => {
                                                        const q = billQty(d, dn)
                                                        const id = `${dn.CodeDevise || ''}-${dn.TypeCoupure || ''}-${dn.Valeur}`
                                                        return (
                                                            <div key={id} className={`eb-borne-denom${q ? ' has-qty' : ''}`}>
                                                                <span className="eb-borne-denom-top">
                                                                    {dn.TypeCoupure === 'PIECE' ? <Coins size={15} /> : <Banknote size={15} />}
                                                                    {dn.CodeDevise && <em>{dn.CodeDevise}</em>}
                                                                </span>
                                                                <b className="eb-borne-denom-val">{formatNombre(dn.Valeur)}</b>
                                                                <span className="eb-borne-denom-step">
                                                                    <button type="button" disabled={!q}
                                                                        onClick={() => { setBillQty(d.op.CodeOperation, dn, q - 1); clearMiss() }}>
                                                                        <Minus size={15} />
                                                                    </button>
                                                                    <b>{q}</b>
                                                                    <button type="button"
                                                                        onClick={() => { setBillQty(d.op.CodeOperation, dn, q + 1); clearMiss() }}>
                                                                        <Plus size={15} />
                                                                    </button>
                                                                </span>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                        {!denoms.length && <span className="muted">Aucune coupure configurée</span>}
                                        {billTotal(d) > 0 && (
                                            <div className="eb-borne-bill-total">
                                                Total : <b>{formatNombre(billTotal(d))}</b>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {error && <p className="eb-borne-error">{error}</p>}
                        <div className="eb-borne-dock">
                            {bilIdx > 0 && (
                                <button className="eb-borne-btn ghost" onClick={prevBil}>
                                    <ArrowLeft size={17} /> Précédent
                                </button>
                            )}
                            {bilIdx < bilOps.length - 1 ? (
                                <button className="eb-borne-btn xl" onClick={nextBil}>
                                    Opération suivante <ArrowRight size={18} />
                                </button>
                            ) : (
                                <button className="eb-borne-btn xl" onClick={() => { if (validateBilletage()) setStep('confirm') }}>
                                    Récapitulatif <ArrowRight size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                )
            })()}

            {/* ---------------- Formulaires KYC post-check-in ---------------- */}
            {step === 'forms' && formTasks[formIdx] && (() => {
                const task = formTasks[formIdx]
                const opLabel = drafts.find((x) => x.op.CodeOperation === task.CodeOperation)?.op.Libelle
                return (
                    <div className="eb-borne-stage">
                        <div className="eb-borne-hero sm">
                            <h1>{task.Libelle}</h1>
                            <p>{task.Description || `Informations complémentaires${opLabel ? ` — ${opLabel}` : ''}`}</p>
                        </div>
                        {formTasks.length > 1 && (
                            <BorneFlowSteps
                                steps={formTasks.map((t) => t.Libelle)}
                                current={formIdx}
                                done={formTasks.map((_, i) => i < formIdx)}
                                onJump={(i) => { setFormIdx(i); setFormVals({}); setMissed({}) }}
                            />
                        )}
                        <div className="eb-borne-details">
                            <div className="eb-borne-detail-card">
                                {task.Champs.map((c) => {
                                    const miss = !!missed[c.CodeChamp]
                                    const val = formVals[c.CodeChamp]
                                    const clearMiss = () => setMissed((m) => (m[c.CodeChamp] ? { ...m, [c.CodeChamp]: false } : m))

                                    if (c.TypeChamp === 'SELECT') return (
                                        <div key={c.CodeChamp} className={`eb-borne-field${miss ? ' is-miss' : ''}`}>
                                            <label>{c.Libelle}{c.Obligatoire ? ' *' : ''}</label>
                                            <div className="eb-borne-chips" role="radiogroup" onFocus={() => setCurField(`form:${c.CodeChamp}`)}>
                                                {parseOptions(c.OptionsJson).map((o) => (
                                                    <button key={o} type="button"
                                                        className={`eb-borne-chip${val === o ? ' is-sel' : ''}`}
                                                        onClick={() => { setFormVals((p) => ({ ...p, [c.CodeChamp]: o })); clearMiss() }}>
                                                        {o}
                                                        {val === o && <CheckCircle2 size={15} />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )

                                    if (c.TypeChamp === 'CHECKBOX') return (
                                        <div key={c.CodeChamp} className={`eb-borne-field${miss ? ' is-miss' : ''}`}>
                                            <label>{c.Libelle}{c.Obligatoire ? ' *' : ''}</label>
                                            <button type="button" className="eb-borne-chip"
                                                style={val ? { borderColor: 'var(--eb-primary)', color: 'var(--eb-primary)' } : undefined}
                                                onClick={() => { setFormVals((p) => ({ ...p, [c.CodeChamp]: !val })); clearMiss() }}>
                                                {val ? <CheckCircle2 size={15} /> : <span style={{ width: 15 }} />}
                                                {c.Placeholder || 'Oui'}
                                            </button>
                                        </div>
                                    )

                                    if (c.TypeChamp === 'DATE') return (
                                        <div key={c.CodeChamp} className={`eb-borne-field${miss ? ' is-miss' : ''}`}>
                                            <label>{c.Libelle}{c.Obligatoire ? ' *' : ''}</label>
                                            <input type="date" value={val || ''}
                                                onChange={(e) => { setFormVals((p) => ({ ...p, [c.CodeChamp]: e.target.value })); clearMiss() }} />
                                        </div>
                                    )

                                    return (
                                        <div key={c.CodeChamp} className={`eb-borne-field${miss ? ' is-miss' : ''}`}>
                                            <label>{c.Libelle}{c.Obligatoire ? ' *' : ''}</label>
                                            <input value={val ?? ''} placeholder={c.Placeholder || c.ValeurDefaut || ''}
                                                readOnly inputMode="none"
                                                onFocus={() => { kbdForForm(c); clearMiss() }}
                                                onClick={() => { kbdForForm(c); clearMiss() }} />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        {error && <p className="eb-borne-error">{error}</p>}
                        <div className="eb-borne-dock">
                            {formIdx > 0 && (
                                <button className="eb-borne-btn ghost" onClick={() => { setFormIdx((i) => i - 1); setFormVals({}); setMissed({}) }}>
                                    <ArrowLeft size={17} /> Précédent
                                </button>
                            )}
                            <button className="eb-borne-btn xl" disabled={loading} onClick={() => submitForm(task)}>
                                {loading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                                {formIdx < formTasks.length - 1 ? 'Formulaire suivant' : 'Terminer'}
                            </button>
                        </div>
                    </div>
                )
            })()}

            {/* ---------------- Confirmation ---------------- */}
            {step === 'confirm' && (
                <div className="eb-borne-stage">
                    <div className="eb-borne-hero sm">
                        <button className="eb-borne-back" onClick={() => setStep(bilOps.length ? 'billetage' : 'details')}><ArrowLeft size={18} /> {bilOps.length ? 'Billetage' : 'Détails'}</button>
                        <h1>Récapitulatif</h1>
                        <p>Vérifiez puis validez votre demande</p>
                    </div>
                    <div className="eb-borne-details">
                        {drafts.map((d) => {
                            const lignes = (d.Billetage || []).filter((b) => b.Quantite > 0)
                            const total = billTotal(d)
                            const montant = d.MontantSaisi ? Number(d.MontantSaisi) : null
                            const ecart = montant != null && total > 0 ? total - montant : null
                            return (
                                <div key={d.op.CodeOperation} className="eb-borne-detail-card recap">
                                    <h3><CheckCircle2 size={18} /> {d.op.Libelle}</h3>
                                    <div className="eb-borne-recap-rows">
                                        {d.NumeroCompteSaisi && (
                                            <div className="eb-borne-recap-row"><span>N° de compte</span><b>{d.NumeroCompteSaisi}</b></div>
                                        )}
                                        {montant != null && (
                                            <div className="eb-borne-recap-row"><span>Montant</span><b>{formatNombre(montant)}</b></div>
                                        )}
                                        {d.CodeOrigine && (
                                            <div className="eb-borne-recap-row"><span>Origine des fonds</span><b>{origineLibelle(d.CodeOrigine)}</b></div>
                                        )}
                                        {d.Motif && (
                                            <div className="eb-borne-recap-row"><span>Motif</span><b>{d.Motif}</b></div>
                                        )}
                                        {!d.NumeroCompteSaisi && montant == null && !d.CodeOrigine && !d.Motif && !lignes.length && (
                                            <span className="muted">Sans complément</span>
                                        )}
                                    </div>
                                    {lignes.length > 0 && (
                                        <div className="eb-borne-recap-bill">
                                            <span className="eb-borne-recap-bill-title"><Banknote size={14} /> Billetage</span>
                                            <div className="eb-borne-recap-denoms">
                                                {[...lignes].sort((a, b) => b.Valeur - a.Valeur).map((b) => (
                                                    <span key={`${b.CodeDevise || ''}-${b.TypeCoupure || ''}-${b.Valeur}`} className="eb-borne-recap-denom">
                                                        <b>{formatNombre(b.Valeur)}</b>
                                                        {b.CodeDevise && <em>{b.CodeDevise}</em>}
                                                        <i>× {b.Quantite}</i>
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="eb-borne-recap-billtotal">
                                                Total billetage <b>{formatNombre(total)}</b>
                                            </div>
                                            {ecart !== null && ecart !== 0 && (
                                                <div className="eb-borne-recap-diff">
                                                    Écart avec le montant déclaré : <b>{ecart > 0 ? '+' : ''}{formatNombre(ecart)}</b>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                    {error && <p className="eb-borne-error">{error}</p>}
                    <div className="eb-borne-dock">
                        <button className="eb-borne-btn xl primary" disabled={loading} onClick={checkIn}>
                            {loading ? <Loader2 size={18} className="animate-spin" /> : <TicketIcon size={18} />}
                            Obtenir mon ticket
                        </button>
                    </div>
                </div>
            )}

            {/* ---------------- Ticket(s) ---------------- */}
            {step === 'ticket' && (
                <div className="eb-borne-stage">
                    <div className="eb-borne-hero">
                        <div className="eb-borne-ticket-icon"><TicketIcon size={44} /></div>
                        <h1>Votre ticket est prêt</h1>
                        <p>Présentez-vous au guichet lorsque votre numéro s'affiche</p>
                    </div>
                    <div className="eb-borne-tickets">
                        {tickets.map((t, i) => (
                            <div key={i} className="eb-borne-ticket" style={{ animationDelay: `${i * 120}ms` }}>
                                <div className="eb-borne-ticket-num">{t.NumeroTicket || t.CodeT}</div>
                                <div className="eb-borne-ticket-svc"><Building2 size={15} /> {t.LibelleService || t.CodeS}</div>
                                <div className="eb-borne-ticket-meta">
                                    {t.Position != null && <span>Position <b>{t.Position}</b></span>}
                                    {t.NbTicketsDevant != null && <span><b>{t.NbTicketsDevant}</b> devant vous</span>}
                                    {t.TempsAttenteEstimeSec != null && <span>Attente ≈ <b>{fmtWait(t.TempsAttenteEstimeSec)}</b></span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                        {drafts.some((d) => billTotal(d) > 0) && tickets.some((t) => t.CodeDemande) && (
                            <button className="eb-borne-btn ghost" onClick={imprimerBordereau}>
                                <Printer size={17} /> Bordereau de billetage
                            </button>
                        )}
                        <button className="eb-borne-btn xl ghost" onClick={resetFlow}>
                            Terminer <CheckCircle2 size={18} />
                        </button>
                    </div>
                </div>
            )}

            {/* Clavier virtuel — s'adapte au champ actif */}
            {kbd && (
                <BorneKeyboard
                    mode={kbd.mode}
                    apply={kbd.apply}
                    preview={kbd.getPreview ? kbd.getPreview() : undefined}
                    previewLabel={kbd.previewLabel}
                    enterHint={kbd.enterHint}
                    onEnter={kbd.onEnter}
                    onClose={() => setKbd(null)}
                />
            )}
        </div>
    );
};

export default SelfServiceBorne;
